import React, { useEffect, useRef, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Input from '@mui/material/Input';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import { Check } from '@mui/icons-material';

const SAVED_INDICATOR_MS = 2000;

// Exercise times save on their own as soon as the field is committed — they are not
// part of the session form, so the form's Save button never applies to them.
const TimeMatrixInput = ({ label, value, disabled, onCommit }) => {
  const [draft, setDraft] = useState(value);
  const [status, setStatus] = useState('idle');
  const savedTimer = useRef(null);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  useEffect(() => () => clearTimeout(savedTimer.current), []);

  const commit = () => {
    const next = Number(draft);
    if (!Number.isFinite(next)) {
      setDraft(value);
      return;
    }
    if (next === value) {
      return;
    }

    setStatus('saving');
    Promise.resolve(onCommit(next))
      .then(() => {
        setStatus('saved');
        savedTimer.current = setTimeout(() => setStatus('idle'), SAVED_INDICATOR_MS);
      })
      .catch(() => {
        // The caller rolls the rejected value back, which leaves `value` identical to
        // what this render already saw — so the sync effect won't restore the field.
        setDraft(value);
        setStatus('idle');
      });
  };

  const renderStatusAdornment = () => {
    if (status === 'idle') {
      return null;
    }
    return (
      <InputAdornment position="end">
        {status === 'saving' ? (
          <CircularProgress size={14} />
        ) : (
          <Check fontSize="small" color="success" titleAccess="Saved" />
        )}
      </InputAdornment>
    );
  };

  return (
    <>
      <InputLabel>{label}</InputLabel>
      <Input
        type="number"
        disabled={disabled}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            // Without this the keypress submits the surrounding session form and the
            // typed time is dropped, because the field never blurs.
            event.preventDefault();
            event.target.blur();
          }
        }}
        endAdornment={renderStatusAdornment()}
      />
    </>
  );
};

export default TimeMatrixInput;
